/**
 * Admin → GoBD-Archiv & Aufbewahrungsfristen.
 * Datengetriebene Übersicht: pro Dokumententyp Anzahl archivierter Belege,
 * konfigurierbare Aufbewahrungsdauer und eine Prüf-/Lösch-Erinnerung für
 * Belege über der gesetzlichen Frist (KEINE automatische Löschung).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Archive, Loader2, Save, ChevronLeft, AlertTriangle, CheckCircle2, X, Download } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const fmtDate = (v) => {
    if (!v) return "—";
    const s = String(v);
    return s.length >= 10 ? s.slice(0, 10) : s;
};

export default function AdminGoBDPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [years, setYears] = useState({});      // key -> string (editable)
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [due, setDue] = useState(null);         // {key,label,items,...}
    const [dueLoading, setDueLoading] = useState(false);
    const [exporting, setExporting] = useState("");  // "<key>-<scope>" während Download

    useEffect(() => {
        if (!authLoading && user?.role !== "admin") navigate("/");
    }, [user, authLoading, navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/gobd/overview");
            setData(data);
            setYears(Object.fromEntries((data.documents || []).map((d) => [d.key, String(d.retention_years)])));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
        } finally { setLoading(false); }
    };
    useEffect(() => { if (user?.role === "admin") load(); }, [user]);

    const dirty = data && (data.documents || []).some((d) => String(d.retention_years) !== years[d.key]);

    const saveConfig = async () => {
        setSaving(true);
        try {
            const config = Object.fromEntries(Object.entries(years).map(([k, v]) => [k, Number(v)]));
            await api.put("/admin/gobd/retention-config", { config });
            toast.success("Aufbewahrungsfristen gespeichert");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    const exportZip = async (key, scope, label) => {
        setExporting(`${key}-${scope}`);
        try {
            const r = await api.get(`/admin/gobd/${key}/export.zip`, { params: { scope }, responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `GoBD_${key}_${scope}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Export „${label}" erstellt`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Export fehlgeschlagen");
        } finally { setExporting(""); }
    };

    const openDue = async (key) => {
        setDueLoading(true); setDue({ key, items: null });
        try {
            const { data } = await api.get(`/admin/gobd/${key}/due`);
            setDue(data);
        } catch (e) {
            toast.error("Fällige Belege konnten nicht geladen werden"); setDue(null);
        } finally { setDueLoading(false); }
    };

    if (authLoading || loading) {
        return <div className="min-h-screen bg-white flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00]" /></div>;
    }

    return (
        <div className="min-h-screen bg-white" data-testid="admin-gobd-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                <Link to="/admin/doku" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#6B7280] hover:text-[#111827]" data-testid="gobd-back">
                    <ChevronLeft className="w-4 h-4" /> Datenschutz-Doku
                </Link>
                <div className="flex items-center gap-3 mt-3 mb-2">
                    <Archive className="w-7 h-7 text-[#FFCC00]" />
                    <h1 className="text-4xl font-black text-[#111827] tracking-tight">GoBD-Archiv &amp; Aufbewahrung</h1>
                </div>
                <p className="text-sm text-[#6B7280] mb-4 max-w-3xl" data-testid="gobd-note">{data?.note}</p>

                {/* Summen */}
                <div className="flex flex-wrap gap-3 mb-6" data-testid="gobd-totals">
                    <Badge className="bg-[#111827] text-white text-xs font-bold">Archivierte Belege: {data?.totals?.documents ?? 0}</Badge>
                    <Badge className={`${(data?.totals?.due ?? 0) > 0 ? "bg-amber-500" : "bg-green-600"} text-white text-xs font-bold`}>
                        Über der Frist (Prüfung): {data?.totals?.due ?? 0}
                    </Badge>
                </div>

                {/* Tabelle */}
                <div className="overflow-x-auto border-2 border-[#111827] rounded-sm" data-testid="gobd-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left bg-gray-50 border-b-2 border-[#111827] text-[11px] uppercase tracking-wider text-gray-600">
                                <th className="py-2.5 px-3">Dokumententyp</th>
                                <th className="py-2.5 px-3">Rechtsgrundlage</th>
                                <th className="py-2.5 px-3 text-right">Belege</th>
                                <th className="py-2.5 px-3">Ältester / Neuester</th>
                                <th className="py-2.5 px-3 text-center">Aufbewahrung (Jahre)</th>
                                <th className="py-2.5 px-3 text-center">Über der Frist</th>
                                <th className="py-2.5 px-3 text-center">Export</th>
                            </tr>
                        </thead>
                        <tbody data-testid="gobd-rows">
                            {(data?.documents || []).map((d) => (
                                <tr key={d.key} className="border-b border-gray-100" data-testid={`gobd-row-${d.key}`}>
                                    <td className="py-2.5 px-3 font-bold text-[#111827]">{d.label}</td>
                                    <td className="py-2.5 px-3 text-gray-500 text-xs">{d.legal}</td>
                                    <td className="py-2.5 px-3 text-right font-mono-num font-bold">{d.total}</td>
                                    <td className="py-2.5 px-3 text-xs text-gray-600 whitespace-nowrap">{fmtDate(d.oldest)} – {fmtDate(d.newest)}</td>
                                    <td className="py-2.5 px-3 text-center">
                                        <Input
                                            type="number" min={data?.min_years} max={data?.max_years}
                                            value={years[d.key] ?? ""}
                                            onChange={(e) => setYears((y) => ({ ...y, [d.key]: e.target.value }))}
                                            className="h-8 w-20 mx-auto text-center rounded-sm font-mono-num"
                                            data-testid={`gobd-years-${d.key}`}
                                        />
                                        {d.retention_years !== d.default_years && (
                                            <div className="text-[10px] text-gray-400 mt-0.5">Standard: {d.default_years}</div>
                                        )}
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                        {d.due_count > 0 ? (
                                            <button type="button" onClick={() => openDue(d.key)}
                                                className="inline-flex items-center gap-1 text-amber-700 font-bold hover:underline"
                                                data-testid={`gobd-due-${d.key}`}>
                                                <AlertTriangle className="w-4 h-4" /> {d.due_count} prüfen
                                            </button>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-green-600" data-testid={`gobd-ok-${d.key}`}>
                                                <CheckCircle2 className="w-4 h-4" /> 0
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => exportZip(d.key, "all", `${d.label} — alle`)}
                                            disabled={exporting === `${d.key}-all` || d.total === 0}
                                            className="rounded-sm border-2 border-[#111827] font-bold uppercase text-[10px] tracking-wider"
                                            data-testid={`gobd-export-${d.key}`}>
                                            {exporting === `${d.key}-all` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> ZIP</>}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Speichern */}
                <div className="mt-5 flex items-center gap-3">
                    <Button onClick={saveConfig} disabled={saving || !dirty}
                        className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase text-xs tracking-wider"
                        data-testid="gobd-save">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Aufbewahrungsfristen speichern
                    </Button>
                    {dirty && <span className="text-xs text-amber-600 font-medium">Ungespeicherte Änderungen</span>}
                </div>

                <p className="text-[11px] text-gray-400 mt-3 max-w-3xl">
                    Aufbewahrung 1–{data?.max_years} Jahre je Typ. Fristbeginn = Schluss des Entstehungsjahres
                    (§ 147 Abs. 4 AO). Die Spalte „Über der Frist" zeigt Belege, deren gesetzliche
                    Aufbewahrungspflicht abgelaufen ist — als Erinnerung; eine automatische Löschung erfolgt nicht.
                </p>

                {/* Fällig-Liste (Prüf-/Lösch-Erinnerung) */}
                {due && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="gobd-due-modal">
                        <div className="bg-white rounded-sm border-2 border-[#111827] max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between gap-2 px-5 py-3 border-b-2 border-[#111827]">
                                <h2 className="font-black text-[#111827] flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-amber-600" /> Über der Frist: {due.label}
                                </h2>
                                <button onClick={() => setDue(null)} data-testid="gobd-due-close"><X className="w-5 h-5 text-gray-500 hover:text-black" /></button>
                            </div>
                            <div className="px-5 py-2 text-xs text-gray-500 border-b flex items-center justify-between gap-2">
                                <span>Aufbewahrung {due.retention_years} Jahre · vor {due.cutoff} entstanden · {due.count ?? 0} Belege</span>
                                {(due.count ?? 0) > 0 && (
                                    <Button variant="outline" size="sm"
                                        onClick={() => exportZip(due.key, "due", `${due.label} — fällige`)}
                                        disabled={exporting === `${due.key}-due`}
                                        className="rounded-sm border-2 border-[#111827] font-bold uppercase text-[10px] tracking-wider"
                                        data-testid="gobd-export-due">
                                        {exporting === `${due.key}-due` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Fällige exportieren</>}
                                    </Button>
                                )}
                            </div>
                            <div className="overflow-y-auto p-2">
                                {dueLoading || due.items === null ? (
                                    <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
                                ) : due.items.length === 0 ? (
                                    <div className="py-6 text-center text-gray-400 text-sm">Keine Belege über der Frist.</div>
                                ) : (
                                    <table className="w-full text-xs" data-testid="gobd-due-rows">
                                        <thead><tr className="text-left text-gray-500 border-b">
                                            <th className="py-1 px-2">Datum</th><th className="py-1 px-2">Referenz</th><th className="py-1 px-2">Händler</th>
                                        </tr></thead>
                                        <tbody>
                                            {due.items.map((it, i) => (
                                                <tr key={i} className="border-b border-gray-100">
                                                    <td className="py-1 px-2 whitespace-nowrap">{fmtDate(it.date)}</td>
                                                    <td className="py-1 px-2 font-mono-num">{it.reference}</td>
                                                    <td className="py-1 px-2 text-gray-400">{it.dealer_id || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
